import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getBlob, ref } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../config/firebaseConfig";
import { IMMICH_BASE_URL } from "../config/immichConfig";
import { syncImmichPersonByUid } from "../services/immichService";
import {
  getAllowedRoles,
  hasPermission,
  PERMISSIONS,
} from "../utils/roleAccess";

import toast from "react-hot-toast";

const EMPTY_FORM_DATA = {
  uid: "",
  name: "",
  className: "",
  phone: "",
  qrImageUrl: "",
  studentImageUrl: "",
  fatherImageUrl: "",
  motherImageUrl: "",
  activeqr: false,
};

const IMMICH_ASSET_UPLOAD_URL = `${IMMICH_BASE_URL}/assets`;
const IMMICH_UPLOAD_TIMEOUT_MS = Number(
  process.env.REACT_APP_IMMICH_UPLOAD_TIMEOUT_MS || 180000,
);
const IMMICH_API_KEY = (process.env.REACT_APP_IMMICH_API_KEY || "").trim();
const IMMICH_DEVICE_LABEL =
  process.env.REACT_APP_IMMICH_DEVICE_ID || "rising-stars-web-admin";

const CLASS_OPTIONS = [
  "Nursery",
  "Nursery_A",
  "Nursery_B",
  "LKG",
  "UKG",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
];

function buildFormState(initialUid = "") {
  return {
    ...EMPTY_FORM_DATA,
    uid: initialUid,
  };
}

function normalizeStudentRecord(studentRecord = {}, fallbackUid = "") {
  const uidValue = String(studentRecord.uid || fallbackUid || "").trim();

  return {
    ...EMPTY_FORM_DATA,
    uid: uidValue,
    name: studentRecord.name || "",
    className: studentRecord.className || "",
    phone: studentRecord.phone || "",
    qrImageUrl: studentRecord.qrImageUrl || "",
    studentImageUrl: studentRecord.studentImageUrl || "",
    fatherImageUrl: studentRecord.fatherImageUrl || "",
    motherImageUrl: studentRecord.motherImageUrl || "",
    activeqr: Boolean(studentRecord.activeqr),
  };
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 45000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function extractResponsePayload(response) {
  const textBody = await response.text();
  let jsonBody = null;

  try {
    jsonBody = textBody ? JSON.parse(textBody) : null;
  } catch {
    jsonBody = null;
  }

  return { textBody, jsonBody };
}

function parseImmichErrorMessage(status, textBody, defaultMessage) {
  const normalizedText = String(textBody || "").trim();

  if (!normalizedText) {
    return `${defaultMessage} (${status})`;
  }

  const shortText =
    normalizedText.length > 220
      ? `${normalizedText.slice(0, 220)}...`
      : normalizedText;

  return `${defaultMessage} (${status}): ${shortText}`;
}

function buildDeviceAssetId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `asset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildImmichUploadPayload(file) {
  const fileTimestamp = new Date(
    file?.lastModified || Date.now(),
  ).toISOString();
  const payload = new FormData();

  payload.append("deviceAssetId", buildDeviceAssetId());
  payload.append("deviceId", IMMICH_DEVICE_LABEL);
  payload.append("fileCreatedAt", fileTimestamp);
  payload.append("fileModifiedAt", fileTimestamp);
  payload.append("assetData", file, file?.name || "photo.jpg");

  return payload;
}

function emitImmichLog(onLog, level, role, message, details = null) {
  if (typeof onLog !== "function") {
    return;
  }

  onLog({
    level,
    role,
    message,
    details,
    timestamp: new Date().toISOString(),
  });
}

function inferImageFileExtension(contentType = "") {
  const normalizedType = String(contentType || "").toLowerCase().trim();

  if (normalizedType.includes("png")) {
    return ".png";
  }

  if (normalizedType.includes("webp")) {
    return ".webp";
  }

  if (normalizedType.includes("gif")) {
    return ".gif";
  }

  if (normalizedType.includes("bmp")) {
    return ".bmp";
  }

  if (normalizedType.includes("tiff")) {
    return ".tif";
  }

  if (normalizedType.includes("heic")) {
    return ".heic";
  }

  if (normalizedType.includes("heif")) {
    return ".heif";
  }

  return ".jpg";
}

function sanitizeFileName(value) {
  const raw = String(value || "");

  return raw
    .replace(/[<>:"/\\|?*]+/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getFileNameFromUrl(url, role = "student", contentType = "") {
  const fallbackBase = `${role}-photo`;
  let candidate = fallbackBase;

  try {
    const parsedUrl = new URL(String(url || ""));
    const segments = parsedUrl.pathname.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1] || "";

    candidate = lastSegment ? decodeURIComponent(lastSegment) : fallbackBase;
  } catch {
    candidate = fallbackBase;
  }

  const baseName = String(candidate)
    .split("/")
    .filter(Boolean)
    .slice(-1)[0];
  const sanitizedBase = sanitizeFileName(baseName || fallbackBase) || fallbackBase;

  if (/\.[a-z0-9]{2,6}$/i.test(sanitizedBase)) {
    return sanitizedBase;
  }

  return `${sanitizedBase}${inferImageFileExtension(contentType)}`;
}

function parseFirebaseStoragePathFromUrl(url) {
  const raw = String(url || "").trim();

  if (!raw) {
    return "";
  }

  if (raw.startsWith("gs://")) {
    const withoutScheme = raw.slice("gs://".length);
    const segments = withoutScheme.split("/").filter(Boolean);

    if (segments.length >= 2) {
      return decodeURIComponent(segments.slice(1).join("/"));
    }
  }

  try {
    const parsedUrl = new URL(raw);
    const host = String(parsedUrl.hostname || "");
    const isFirebaseStorageHost = host.includes("firebasestorage.googleapis.com");
    const isGcsHost = host === "storage.googleapis.com";

    if (!isFirebaseStorageHost && !isGcsHost) {
      return "";
    }

    const objectPathMarker = "/o/";
    const markerIndex = parsedUrl.pathname.indexOf(objectPathMarker);

    if (markerIndex !== -1) {
      const encodedObjectPath = parsedUrl.pathname.slice(
        markerIndex + objectPathMarker.length,
      );

      return decodeURIComponent(encodedObjectPath || "");
    }

    if (isGcsHost) {
      const segments = parsedUrl.pathname.split("/").filter(Boolean);
      if (segments.length >= 2) {
        return decodeURIComponent(segments.slice(1).join("/"));
      }
    }

    return "";
  } catch {
    return "";
  }
}

async function getImageBlobForImmich(imageUrl, role = "student", onLog = null) {
  const normalizedUrl = String(imageUrl || "").trim();
  const storagePath = parseFirebaseStoragePathFromUrl(normalizedUrl);

  if (storagePath) {
    try {
      emitImmichLog(
        onLog,
        "info",
        role,
        "Fetching image via Firebase Storage SDK",
        {
          storagePath,
        },
      );

      return await getBlob(ref(storage, storagePath));
    } catch (error) {
      emitImmichLog(
        onLog,
        "warning",
        role,
        "Firebase SDK image fetch failed, falling back to URL fetch",
        {
          storagePath,
          message: error?.message || "Unknown error",
        },
      );
    }
  }

  const response = await fetchWithTimeout(
    normalizedUrl,
    { method: "GET" },
    IMMICH_UPLOAD_TIMEOUT_MS,
  );

  if (!response.ok) {
    throw new Error(
      parseImmichErrorMessage(response.status, "", `Failed to fetch ${role} image URL`),
    );
  }

  return response.blob();
}

async function fetchImageFileFromUrl(imageUrl, role = "student", onLog = null) {
  const normalizedUrl = String(imageUrl || "").trim();

  if (!normalizedUrl) {
    return null;
  }

  emitImmichLog(onLog, "info", role, "Fetching image for Immich upload", {
    imageUrl: normalizedUrl,
  });

  const blob = await getImageBlobForImmich(normalizedUrl, role, onLog);
  const fileType = blob?.type || "image/jpeg";
  const fileName = getFileNameFromUrl(normalizedUrl, role, fileType);

  return new File([blob], fileName, {
    type: fileType,
    lastModified: Date.now(),
  });
}

async function readImageDimensionsFromFile(file) {
  if (!file) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        imageWidth: image.naturalWidth || image.width || 0,
        imageHeight: image.naturalHeight || image.height || 0,
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read image dimensions."));
    };

    image.src = objectUrl;
  });
}

async function uploadImmichAssetFile(file) {
  const uploadRes = await fetchWithTimeout(
    IMMICH_ASSET_UPLOAD_URL,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "x-api-key": IMMICH_API_KEY,
      },
      body: buildImmichUploadPayload(file),
    },
    IMMICH_UPLOAD_TIMEOUT_MS,
  );

  const { textBody, jsonBody } = await extractResponsePayload(uploadRes);

  if (!uploadRes.ok) {
    throw new Error(
      parseImmichErrorMessage(uploadRes.status, textBody, "Immich asset upload failed"),
    );
  }

  const assetId = jsonBody?.id || jsonBody?.assetId || jsonBody?.asset?.id;
  if (!assetId) {
    throw new Error("Immich upload did not return an asset id.");
  }

  return assetId;
}

export default function OnboardStudentForm({
  initialUid = "",
  initialStudentRecord = null,
}) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(() =>
    initialStudentRecord
      ? normalizeStudentRecord(initialStudentRecord, initialUid)
      : buildFormState(initialUid),
  );
  const [existingStudent, setExistingStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [canManageOnboarding, setCanManageOnboarding] = useState(false);
  const [, setImmichLogs] = useState([]);

  const lockedUid = String(
    initialUid || initialStudentRecord?.uid || "",
  ).trim();
  const mode = existingStudent ? "update" : "create";

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setCanManageOnboarding(false);
        setLoading(false);
        return;
      }

      setUser(currentUser);

      const userReference = doc(db, "users", currentUser.uid);
      const userSnapshot = await getDoc(userReference);

      if (!userSnapshot.exists()) {
        setCanManageOnboarding(false);
        setLoading(false);
        return;
      }

      const userData = userSnapshot.data();
      setCanManageOnboarding(
        hasPermission(userData, PERMISSIONS.STUDENT_ONBOARDING),
      );
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let active = true;

    async function loadStudentByUid() {
      const uidValue = String(
        initialUid || initialStudentRecord?.uid || "",
      ).trim();

      if (initialStudentRecord && active) {
        const normalizedStudent = normalizeStudentRecord(
          initialStudentRecord,
          uidValue,
        );

        setExistingStudent(normalizedStudent);
        setFormData((currentValue) => ({
          ...currentValue,
          ...normalizedStudent,
          uid: normalizedStudent.uid,
        }));
      }

      if (!uidValue) {
        setExistingStudent(null);
        setFormData(buildFormState());
        return;
      }

      const studentQuery = query(
        collection(db, "students"),
        where("uid", "==", uidValue),
      );
      const snapshot = await getDocs(studentQuery);

      if (!active) {
        return;
      }

      if (snapshot.empty) {
        const fallbackStudent = initialStudentRecord
          ? normalizeStudentRecord(initialStudentRecord, uidValue)
          : null;

        setExistingStudent(fallbackStudent);
        setFormData(
          fallbackStudent ? fallbackStudent : buildFormState(uidValue),
        );
        return;
      }

      const studentRecord = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      };

      setExistingStudent(studentRecord);
      setFormData(normalizeStudentRecord(studentRecord, uidValue));
    }

    loadStudentByUid();

    return () => {
      active = false;
    };
  }, [initialStudentRecord, initialUid]);

  const summaryRows = useMemo(
    () => [
      { label: "UID", value: formData.uid || "Not available" },
      { label: "Student Name", value: formData.name || "Not available" },
      { label: "Class", value: formData.className || "Not available" },
      { label: "Phone", value: formData.phone || "Not available" },
      {
        label: "Current QR Status",
        value: existingStudent?.activeqr ? "Active" : "Inactive",
      },
    ],
    [
      existingStudent?.activeqr,
      formData.className,
      formData.name,
      formData.phone,
      formData.uid,
    ],
  );

  const classOptions = useMemo(() => {
    if (!formData.className || CLASS_OPTIONS.includes(formData.className)) {
      return CLASS_OPTIONS;
    }

    return [formData.className, ...CLASS_OPTIONS];
  }, [formData.className]);

  function handleChange(event) {
    const { name, value } = event.target;
    setFormData((currentValue) => ({
      ...currentValue,
      [name]: value,
    }));
  }

  function addImmichLog(entry) {
    setImmichLogs((currentValue) => [
      ...currentValue.slice(-119),
      {
        ...entry,
        id:
          typeof crypto !== "undefined" &&
          typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      },
    ]);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const uidValue = String(formData.uid || "").trim();

    if (!uidValue) {
      toast.error("Student UID is required");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Please enter student name");
      return;
    }

    if (!formData.className.trim()) {
      toast.error("Class is required.");
      return;
    }

    if (!formData.qrImageUrl.trim()) {
      toast.error("QR Image URL is required.");
      return;
    }

    setSaving(true);
    setImmichLogs([]);
    addImmichLog({
      level: "info",
      role: "system",
      message: `Starting onboarding sync for UID ${uidValue}`,
      details: null,
      timestamp: new Date().toISOString(),
    });

    try {
      const studentQuery = query(
        collection(db, "students"),
        where("uid", "==", uidValue),
      );
      const snapshot = await getDocs(studentQuery);
      const roleImageConfigs = [
        {
          role: "student",
          displayName: "",
          imageUrl: formData.studentImageUrl,
        },
        {
          role: "father",
          displayName: "",
          imageUrl: formData.fatherImageUrl,
        },
        {
          role: "mother",
          displayName: "",
          imageUrl: formData.motherImageUrl,
        },
      ];

      const peopleSyncResults = await Promise.all(
        roleImageConfigs.map(async ({ role, displayName, imageUrl }) => {
          const normalizedImageUrl = String(imageUrl || "").trim();

          if (!normalizedImageUrl) {
            addImmichLog({
              level: "warning",
              role,
              message: `Skipped ${role} sync: no image URL available`,
              details: null,
              timestamp: new Date().toISOString(),
            });

            return {
              role,
              result: {
                status: "skipped",
                message: `No ${role} image URL available for Immich sync.`,
                personCreateSkipped: true,
              },
            };
          }

          if (!IMMICH_API_KEY) {
            return {
              role,
              result: {
                status: "skipped",
                message: "Missing REACT_APP_IMMICH_API_KEY, skipped Immich sync.",
                personCreateSkipped: true,
              },
            };
          }

          try {
            const imageFile = await fetchImageFileFromUrl(
              normalizedImageUrl,
              role,
              addImmichLog,
            );
            const [assetId, imageDimensions] = await Promise.all([
              uploadImmichAssetFile(imageFile),
              readImageDimensionsFromFile(imageFile),
            ]);

            const result = await syncImmichPersonByUid(uidValue, role, {
              onLog: addImmichLog,
              displayName,
              faceAsset:
                assetId && imageDimensions
                  ? {
                      assetId,
                      imageWidth: imageDimensions.imageWidth,
                      imageHeight: imageDimensions.imageHeight,
                    }
                  : null,
            });

            return {
              role,
              result: {
                ...result,
                assetId,
              },
            };
          } catch (error) {
            return {
              role,
              result: {
                status: "failed",
                message: error?.message || `Failed ${role} Immich sync`,
                personCreateSkipped: false,
              },
            };
          }
        }),
      );

      const immichStudentSync =
        peopleSyncResults.find((item) => item.role === "student")?.result || {
          status: "skipped",
        };
      const immichFatherSync =
        peopleSyncResults.find((item) => item.role === "father")?.result || {
          status: "skipped",
        };
      const immichMotherSync =
        peopleSyncResults.find((item) => item.role === "mother")?.result || {
          status: "skipped",
        };
      const syncSummary = {
        success: peopleSyncResults.filter(
          (item) => item.result.status === "success",
        ),
        failed: peopleSyncResults.filter(
          (item) => item.result.status === "failed",
        ),
        skipped: peopleSyncResults.filter(
          (item) => item.result.status === "skipped",
        ),
      };
      const immichMetadata = {};

      if (immichStudentSync.status === "success") {
        immichMetadata.immichStudentPersonSyncStatus = "completed";
        immichMetadata.immichStudentPersonSyncedAt = Timestamp.now();
        immichMetadata.immichStudentPersonName =
          immichStudentSync.personName || uidValue;

        if (immichStudentSync.personId) {
          immichMetadata.immichStudentPersonId = immichStudentSync.personId;
        }

        if (immichStudentSync.personCreateUrl) {
          immichMetadata.immichStudentPersonCreateUrl =
            immichStudentSync.personCreateUrl;
        }

        immichMetadata.immichStudentPersonCreateSkipped = Boolean(
          immichStudentSync.personCreateSkipped,
        );
        immichMetadata.immichStudentPersonSyncError = "";
      } else if (immichStudentSync.status === "failed") {
        immichMetadata.immichStudentPersonSyncStatus = "failed";
        immichMetadata.immichStudentPersonSyncError = immichStudentSync.message;
        immichMetadata.immichStudentPersonSyncedAt = Timestamp.now();
      } else if (immichStudentSync.status === "skipped") {
        immichMetadata.immichStudentPersonSyncStatus = "skipped";
      }

      if (immichFatherSync.status === "success") {
        immichMetadata.immichFatherPersonName =
          immichFatherSync.personName || `${uidValue}_father`;
        immichMetadata.immichFatherPersonId = immichFatherSync.personId || "";
        immichMetadata.immichFatherPersonCreateSkipped = Boolean(
          immichFatherSync.personCreateSkipped,
        );
        immichMetadata.immichFatherPersonSyncError = "";
      } else if (immichFatherSync.status === "failed") {
        immichMetadata.immichFatherPersonSyncError = immichFatherSync.message;
      }

      if (immichMotherSync.status === "success") {
        immichMetadata.immichMotherPersonName =
          immichMotherSync.personName || `${uidValue}_mother`;
        immichMetadata.immichMotherPersonId = immichMotherSync.personId || "";
        immichMetadata.immichMotherPersonCreateSkipped = Boolean(
          immichMotherSync.personCreateSkipped,
        );
        immichMetadata.immichMotherPersonSyncError = "";
      } else if (immichMotherSync.status === "failed") {
        immichMetadata.immichMotherPersonSyncError = immichMotherSync.message;
      }

      if (syncSummary.failed.length === peopleSyncResults.length) {
        immichMetadata.immichPeopleSyncStatus = "failed";
      } else if (syncSummary.failed.length > 0) {
        immichMetadata.immichPeopleSyncStatus = "partial";
      } else if (syncSummary.success.length > 0) {
        immichMetadata.immichPeopleSyncStatus = "completed";
      } else {
        immichMetadata.immichPeopleSyncStatus = "skipped";
      }

      immichMetadata.immichPeopleSyncedAt = Timestamp.now();
      immichMetadata.immichPeopleSyncResults = peopleSyncResults.map(
        ({ role, result }) => ({
          role,
          status: result.status,
          personName: result.personName || "",
          personId: result.personId || "",
          message: result.message || "",
          personCreateSkipped: Boolean(result.personCreateSkipped),
        }),
      );

      addImmichLog({
        level: "info",
        role: "system",
        message: "Immich sync summary",
        details: {
          success: syncSummary.success.length,
          failed: syncSummary.failed.length,
          skipped: syncSummary.skipped.length,
        },
        timestamp: new Date().toISOString(),
      });

      const onboardingData = {
        ...formData,
        uid: uidValue,
        qrImageUrl: formData.qrImageUrl.trim(),
        activeqr: true,
        ...immichMetadata,
      };

      let successMessage = "";

      if (!snapshot.empty) {
        const existingRecord = snapshot.docs[0];

        await updateDoc(existingRecord.ref, {
          ...existingRecord.data(),
          ...onboardingData,
          updatedAt: Timestamp.now(),
        });

        setExistingStudent({
          id: existingRecord.id,
          ...existingRecord.data(),
          ...onboardingData,
        });
        successMessage = "Student onboarded successfully!";
      } else {
        const documentReference = await addDoc(collection(db, "students"), {
          ...onboardingData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });

        setExistingStudent({
          id: documentReference.id,
          ...onboardingData,
        });
        successMessage = "New student added and onboarded successfully!";
      }

      if (syncSummary.failed.length > 0) {
        const failedSummary = syncSummary.failed
          .map((item) => `${item.role}: ${item.result.message}`)
          .join(" | ");

        toast.success(successMessage);
        toast("Some sync operations failed. Check logs.", { icon: "⚠️" });

        console.error("Immich sync failed:", failedSummary);
      } else if (syncSummary.skipped.length > 0) {
        toast.success(successMessage);
        toast("Some data was skipped during sync", { icon: "ℹ️" });
      } else {
        toast.success(successMessage);
      }

      addImmichLog({
        level: "success",
        role: "system",
        message: "Firebase save completed",
        details: {
          uid: uidValue,
          mode,
          immichPeopleSyncStatus: immichMetadata.immichPeopleSyncStatus,
        },
        timestamp: new Date().toISOString(),
      });

      setFormData((currentValue) => ({
        ...currentValue,
        uid: uidValue,
        activeqr: true,
      }));

      navigate(`/update-student?uid=${encodeURIComponent(uidValue)}`);
    } catch (error) {
      addImmichLog({
        level: "error",
        role: "system",
        message: "Onboarding submission failed",
        details: {
          message: error.message,
        },
        timestamp: new Date().toISOString(),
      });
      toast.error("Something went wrong. Please try again");

      console.error("Onboarding error:", error);
    } finally {
      setSaving(false);
    }
  }

  const editableInputClass =
    "w-full rounded-xl border border-[#a5f3fc] bg-[#f0fdfa] px-4 py-3 text-sm text-[#0f172a] outline-none transition focus:border-[#14b8a6] focus:bg-white focus:ring-2 focus:ring-[#99f6e4]";

  const readOnlyInputClass =
    "w-full cursor-not-allowed rounded-xl border border-[#cbd5e1] bg-[#e2e8f0] px-4 py-3 text-sm text-[#475569] outline-none";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#ecfeff] via-[#eff6ff] to-[#f0fdfa] p-4">
        <div className="rounded-3xl border border-white/60 bg-white/90 px-8 py-7 shadow-2xl backdrop-blur">
          <p className="text-sm font-semibold text-[#155e75]">
            Loading onboarding form...
          </p>
        </div>
      </div>
    );
  }

  if (!user || !canManageOnboarding) {
    const allowedRoles = getAllowedRoles(PERMISSIONS.STUDENT_ONBOARDING).join(
      ", ",
    );

    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#ecfeff] via-[#eff6ff] to-[#f0fdfa] p-4">
        <div className="max-w-md rounded-3xl border border-white/60 bg-white/90 p-8 text-center shadow-2xl backdrop-blur">
          <h1 className="text-2xl font-semibold text-[#0f172a]">
            Access required
          </h1>
          <p className="mt-3 text-sm leading-6 text-[#334155]">
            Users with one of these roles can open the onboarding form:{" "}
            {allowedRoles || "admin"}.
          </p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="mt-6 rounded-xl bg-gradient-to-r from-[#0f766e] to-[#1d4ed8] px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:from-[#115e59] hover:to-[#1e40af]"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#ecfeff] via-[#eff6ff] to-[#fef9c3] px-4 py-6 sm:px-6 lg:px-8"
      style={{ fontFamily: '"Poppins", "Segoe UI", sans-serif' }}
    >
      <div className="mx-auto max-w-5xl">
        <div className="rounded-[2rem] border border-white/30 bg-gradient-to-r from-[#0f172a] via-[#134e4a] to-[#1d4ed8] p-7 text-white shadow-2xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-100">
                Student Onboarding
              </p>
              <h1 className="mt-2 text-3xl font-semibold text-white sm:text-4xl">
                {mode === "update"
                  ? "Update And Onboard Student"
                  : "Add And Onboard Student"}
              </h1>
              a
              <p className="mt-2 max-w-3xl text-sm leading-6 text-cyan-100">
                Load aby URI, update required fields, then click onboard to
                save.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate("/student-onbarding")}
                className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#0f172a] shadow-lg transition hover:bg-cyan-50"
              >
                Back to Queue
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin-dashboard")}
                className="rounded-xl border border-white/30 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/20"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.08fr_0.92fr]">
          <form
            onSubmit={handleSubmit}
            className="rounded-[2rem] border border-[#bae6fd] bg-white/90 p-6 shadow-2xl backdrop-blur"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-semibold text-[#0f766e]">
                  Student UID
                </label>
                <input
                  type="text"
                  name="uid"
                  value={formData.uid}
                  onChange={handleChange}
                  readOnly={Boolean(lockedUid)}
                  className={
                    Boolean(lockedUid) ? readOnlyInputClass : editableInputClass
                  }
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-[#0f766e]">
                  Student Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={editableInputClass}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-[#0f766e]">
                  Class
                </label>
                <select
                  name="className"
                  value={formData.className}
                  onChange={handleChange}
                  className={editableInputClass}
                >
                  <option value="">Select Class</option>
                  {classOptions.map((className) => (
                    <option key={className} value={className}>
                      {className}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-[#0f766e]">
                  Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={editableInputClass}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-semibold text-[#0f766e]">
                  QR Image URL
                </label>
                <input
                  type="url"
                  name="qrImageUrl"
                  value={formData.qrImageUrl}
                  onChange={handleChange}
                  placeholder="https://..."
                  className={editableInputClass}
                />
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-[#99f6e4] bg-[#f0fdfa] px-4 py-3 text-sm text-[#0f766e]">
              `activeqr` stays unchanged until you click the onboarding button
              below. On submit, the student will be saved and marked active.
            </div>

            <button
              type="submit"
              disabled={saving}
              className={`mt-6 w-full rounded-xl px-6 py-3 text-base font-semibold text-white shadow-lg transition ${
                saving
                  ? "cursor-not-allowed bg-slate-400"
                  : "bg-gradient-to-r from-[#0f766e] to-[#1d4ed8] hover:from-[#115e59] hover:to-[#1e40af]"
              }`}
            >
              {saving
                ? "Saving Onboarding..."
                : mode === "update"
                  ? "Update And Onboard Student"
                  : "Add And Onboard Student"}
            </button>
          </form>

          <div className="rounded-[2rem] border border-[#bae6fd] bg-white/90 p-6 shadow-2xl backdrop-blur">
            <h2 className="text-lg font-semibold text-[#0f172a]">
              Loaded Student Details
            </h2>
            <p className="mt-1 text-sm text-[#475569]">
              These are the important fields loaded into the onboarding form.
            </p>

            <div className="mt-5 space-y-3">
              {summaryRows.map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-[#a5f3fc] bg-[#f0fdfa] px-4 py-3"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#0f766e]">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-medium text-[#0f172a]">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

            {formData.qrImageUrl ? (
              <div className="mt-5 rounded-2xl border border-[#a5f3fc] bg-[#f0fdfa] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#0f766e]">
                  QR Preview
                </p>
                <img
                  src={formData.qrImageUrl}
                  alt="QR Preview"
                  className="mt-3 h-40 w-40 rounded-2xl border border-[#67e8f9] bg-white p-3 object-contain"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : null}

            {/* <div className="mt-5 rounded-2xl border border-[#c7d2fe] bg-[#eef2ff] p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#3730a3]">
                  Immich Background Logs
                </p>
                <button
                  type="button"
                  onClick={() => setImmichLogs([])}
                  className="rounded-md border border-[#c7d2fe] bg-white px-2 py-1 text-xs font-semibold text-[#3730a3] transition hover:bg-[#e0e7ff]"
                >
                  Clear
                </button>
              </div>

              {immichLogs.length === 0 ? (
                <p className="mt-3 text-sm text-[#475569]">
                  No logs yet. Submit onboarding to see Immich request/response
                  activity.
                </p>
              ) : (
                <div className="mt-3 max-h-80 space-y-2 overflow-auto pr-1">
                  {immichLogs.map((logItem) => {
                    const levelClass =
                      IMMICH_LOG_LEVEL_STYLES[logItem.level] ||
                      IMMICH_LOG_LEVEL_STYLES.info;
                    const timeLabel = new Date(
                      logItem.timestamp || Date.now(),
                    ).toLocaleTimeString();

                    return (
                      <div
                        key={logItem.id}
                        className={`rounded-xl border px-3 py-2 ${levelClass}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.12em]">
                            {logItem.role || "system"} | {logItem.level}
                          </p>
                          <p className="text-[11px] font-medium opacity-80">
                            {timeLabel}
                          </p>
                        </div>
                        <p className="mt-1 text-sm font-medium">
                          {logItem.message}
                        </p>
                        {logItem.details ? (
                          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-lg bg-white/80 p-2 text-[11px] leading-4 text-[#1e293b]">
                            {JSON.stringify(logItem.details, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}
